import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import {
  Users,
  Plus,
  Search,
  Heart,
  MessageSquare,
  Eye,
  UserCheck,
  BookOpen,
  HelpCircle,
  Megaphone,
} from 'lucide-react';
import { communityApi, CommunityPost } from '../services/communityApi';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';

const POST_TYPE_CONFIG = {
  article: { label: 'community.postType.article', icon: BookOpen, color: 'bg-blue-100 text-blue-700' },
  question: { label: 'community.postType.question', icon: HelpCircle, color: 'bg-yellow-100 text-yellow-700' },
  discussion: { label: 'community.postType.discussion', icon: MessageSquare, color: 'bg-green-100 text-green-700' },
  announcement: { label: 'community.postType.announcement', icon: Megaphone, color: 'bg-purple-100 text-purple-700' },
};

export const CommunityChannelPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const { data: channels = [] } = useQuery({
    queryKey: ['community-channels'],
    queryFn: communityApi.getChannels,
  });

  const channel = channels.find((c) => c.slug === slug);

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['community-channel-posts', channel?.id, activeSearch],
    queryFn: () =>
      communityApi.getPosts({
        category_id: channel?.id,
        search: activeSearch || undefined,
        limit: 30,
      }),
    enabled: !!channel?.id,
  });

  const joinMutation = useMutation({
    mutationFn: communityApi.joinChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-channels'] });
      toast.success(t('community.joinedChannel'));
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const leaveMutation = useMutation({
    mutationFn: communityApi.leaveChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-channels'] });
      toast.success(t('community.leftChannel'));
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearch(search);
  };

  if (channels.length > 0 && !channel) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="text-center py-10">
            <p className="text-gray-500">{t('communityChannel.notFound')}</p>
            <Link to="/community" className="text-[#0B1F3A] hover:underline mt-2 inline-block">
              {t('communityChannel.backToCommunity')}
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/community" className="hover:text-[#0B1F3A]">
          {t('community.title')}
        </Link>
        <span>›</span>
        <span className="text-gray-900 font-medium">{channel ? t(`screening.types.${channel.professional_type}`, { defaultValue: channel.name }) : slug}</span>
      </div>

      {/* Channel header */}
      {channel && (
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
              style={{ backgroundColor: channel.color }}
            >
              {t(`screening.types.${channel.professional_type}`, { defaultValue: channel.name }).slice(0, 1)}
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{t(`screening.types.${channel.professional_type}`, { defaultValue: channel.name })}</h1>
              <p className="text-sm text-gray-600 mt-1">{channel.description}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> {channel.member_count} {t('community.members')}
                </span>
              </div>
            </div>
            <div className="flex-shrink-0">
              {channel.is_member ? (
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 text-sm text-green-600">
                    <UserCheck className="w-4 h-4" /> {t('communityChannel.subscribed')}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => leaveMutation.mutate(channel.id)}
                    loading={leaveMutation.isPending}
                  >
                    {t('community.unsubscribe')}
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  onClick={() => joinMutation.mutate(channel.id)}
                  loading={joinMutation.isPending}
                >
                  {t('community.subscribe')}
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Search + new post */}
      <div className="flex gap-2 mb-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('communityChannel.searchInChannel', { name: channel ? t(`screening.types.${channel.professional_type}`, { defaultValue: channel.name }) : '' })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            {t('community.search')}
          </Button>
          {activeSearch && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setActiveSearch(''); setSearch(''); }}
            >
              {t('community.clear')}
            </Button>
          )}
        </form>
        <Button
          size="sm"
          onClick={() =>
            navigate('/community/nieuw-bericht', {
              state: { category_id: channel?.id, category_name: channel?.name },
            })
          }
        >
          <Plus className="w-4 h-4 mr-1" />
          {t('community.newPost')}
        </Button>
      </div>

      {/* Posts */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <MessageSquare className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500">
              {activeSearch ? t('communityChannel.noPostsFound') : t('communityChannel.noPostsYet')}
            </p>
            <Button
              className="mt-4"
              size="sm"
              onClick={() =>
                navigate('/community/nieuw-bericht', {
                  state: { category_id: channel?.id, category_name: channel?.name },
                })
              }
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('communityChannel.firstPost')}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <ChannelPostCard key={post.id} post={post} />
          ))}
        </div>
      )}
    </div>
  );
};

const ChannelPostCard: React.FC<{ post: CommunityPost }> = ({ post }) => {
  const { t } = useTranslation('pages');
  const typeConf = POST_TYPE_CONFIG[post.type] || POST_TYPE_CONFIG.discussion;
  const TypeIcon = typeConf.icon;
  const excerptText = post.excerpt || '';
  const isLong = excerptText.length > 150;
  const displayExcerpt = isLong ? excerptText.slice(0, 150) + '…' : excerptText;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#0B1F3A] hover:shadow-sm transition-all">
      {post.is_pinned && (
        <div className="flex items-center gap-1 text-xs text-[#E5A425] font-medium mb-2">
          <span>{t('communityChannel.pinned')}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${typeConf.color}`}>
            <TypeIcon className="w-3 h-3" />
            {t(typeConf.label)}
          </span>
        </div>
        <Link to={`/community/bericht/${post.id}`} className="group">
          <h3 className="font-semibold text-gray-900 group-hover:text-[#0B1F3A] transition-colors">
            {post.title}
          </h3>
        </Link>
        {post.featured_image && (
          <Link to={`/community/bericht/${post.id}`} className="block mt-2 rounded-md overflow-hidden">
            <img src={post.featured_image} alt={post.title} className="w-full h-32 object-cover" />
          </Link>
        )}
        {displayExcerpt && (
          <p className="text-sm text-gray-500 mt-1">
            {displayExcerpt}
            {isLong && (
              <Link
                to={`/community/bericht/${post.id}`}
                className="ml-1 text-[#0B1F3A] hover:underline font-medium whitespace-nowrap"
              >
                {t('community.readMore')}
              </Link>
            )}
          </p>
        )}
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
        <span>
          {post.first_name} {post.last_name} ·{' '}
          {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: nl })}
        </span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Heart className="w-3.5 h-3.5" /> {post.likes_count}
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare className="w-3.5 h-3.5" /> {post.comments_count}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="w-3.5 h-3.5" /> {post.views_count}
          </span>
        </div>
      </div>
    </div>
  );
};
